import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { isRefusal, refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { FileCheckMessage, FileEntry, FilePreview, FilePreviewEntry, FilesStatus } from "../../../api/types";
import { useLive } from "../../../live";
import { Banner, Button, Card, EmptyState, Loading, Pill, StateBadge, displayState } from "../../../ui";
import { Check, FormError } from "../components/fields";
import { ElementLink } from "../components/panels";
import { StateBar } from "../components/StateBar";
import { useRefresh } from "../hooks";
import { filesStatusWords, pickFilesStatus } from "../lib/filters";
import { chapterKeyOfFile, chapterPath } from "../lib/paths";
import { FILE_KIND_ORDER, FILE_KIND_WORDS, kindWord } from "../lib/words";
import type { ElementState } from "../../../api/types";

/** The screen at /files: the app's battery limit, one file per item then the shared files, with a preview. */
export function FilesPage() {
  const files = useApiQuery("files_view", {});
  const live = useLive();
  const write = useApiCommand("request_files_write");
  const refresh = useRefresh();
  const [selected, setSelected] = useState<string | null>(null);
  const [validatedOnly, setValidatedOnly] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  // On a narrow screen the preview is under the list, far from the file that was chosen: bring it into view and put focus on it.
  // On a wide screen it stays beside the list (it is sticky), so nothing moves.
  useEffect(() => {
    if (!selected) return;
    const narrow = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 860px)").matches;
    if (!narrow) return;
    previewRef.current?.scrollIntoView?.({ block: "start" });
    previewRef.current?.focus({ preventScroll: true });
  }, [selected]);

  const status: FilesStatus | null = pickFilesStatus(files.data, live.files);
  const grouped = FILE_KIND_ORDER.map((kind) => ({ kind, files: (files.data?.files ?? []).filter((file) => file.kind === kind) })).filter((group) => group.files.length > 0);
  const others = (files.data?.files ?? []).filter((file) => !FILE_KIND_ORDER.includes(file.kind));

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="files-title">
        <h1 id="files-title">Files</h1>
        <p>
          The files the app writes from what was decided. Only validated content is ready to hand on.
        </p>
        {status && (
          <p className="files-status" data-status={status.status}>
            <Pill tone={status.status === "up_to_date" ? "note" : status.status === "check_failed" ? "problem" : "neutral"}>{filesStatusWords(status)}</Pill>
            {status.release != null && <span className="muted"> Release {status.release}</span>}
          </p>
        )}
        {status?.status === "check_failed" && (
          <Banner tone="warning">
            <p>{status.message || "The last write failed its check. The files you see are the last good ones; nothing you did was lost."}</p>
            {status.elements && status.elements.length > 0 && (
              <>
                <p>The elements involved:</p>
                <ul>
                  {status.elements.map((id) => (
                    <li key={id}>
                      <ElementLink id={id} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            <CheckMessages messages={status.messages ?? []} summary="Details: what the check said" />
          </Banner>
        )}
        <div className="row">
          <Button
            variant="primary"
            busy={write.isPending || status?.status === "writing"}
            onClick={() =>
              write.mutate(
                {},
                {
                  onSuccess: () => refresh("files_view", "file_preview"),
                },
              )
            }
          >
            Write the files now
          </Button>
        </div>
        <FormError message={write.error ? refusalText(write.error) : null} />
      </Card>

      {files.isPending ? (
        <Loading>Reading the list of files</Loading>
      ) : files.isError ? (
        <Card>
          <EmptyState title="The files could not be listed">{refusalText(files.error)}</EmptyState>
        </Card>
      ) : files.data.files.length === 0 ? (
        <Card>
          <EmptyState title="No files have been written yet">The files are written after a batch is settled, or when you press &quot;Write the files now&quot;.</EmptyState>
        </Card>
      ) : (
        <div className="files-layout">
          <div className="files-layout__list stack">
            {[...grouped, ...(others.length > 0 ? [{ kind: "other", files: others }] : [])].map((group) => (
              <Card key={group.kind} aria-label={FILE_KIND_WORDS[group.kind] ?? "Other files"}>
                <h2>{FILE_KIND_WORDS[group.kind] ?? "Other files"}</h2>
                <ul className="file-list">
                  {group.files.map((file) => (
                    <FileRow key={file.path} file={file} selected={selected === file.path} onOpen={() => setSelected(file.path)} />
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <div className="files-layout__preview" ref={previewRef} tabIndex={-1}>
            {selected ? (
              <Preview path={selected} validatedOnly={validatedOnly} onValidatedOnly={setValidatedOnly} onClose={() => setSelected(null)} />
            ) : (
              <Card>
                <EmptyState title="Choose a file to preview">The preview shows each element&apos;s state, discipline and what needs it.</EmptyState>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** What the file check said, file by file, behind a disclosure (the tool's own words, as plain text). */
export function CheckMessages({ messages, summary }: { messages: FileCheckMessage[]; summary: string }) {
  if (messages.length === 0) return null;
  return (
    <details className="check-messages">
      <summary>{summary}</summary>
      <ul>
        {messages.map((m, index) => (
          <li key={`${m.file}:${index}`}>
            {m.file ? <code>{m.file}</code> : null} {m.tool ? <span className="muted">({m.tool})</span> : null} {m.text}
          </li>
        ))}
      </ul>
    </details>
  );
}

function FileRow({ file, selected, onOpen }: { file: FileEntry; selected: boolean; onOpen: () => void }) {
  return (
    <li className="file" data-selected={selected || undefined}>
      <div className="file__head">
        <Button variant="quiet" aria-pressed={selected} onClick={onOpen}>
          <code>{file.path}</code>
        </Button>
        <span className="muted">{file.element_count === 1 ? "1 element" : `${file.element_count} elements`}</span>
      </div>
      <StateBar states={file.states} label={`State of ${file.path}`} empty={file.element_count > 0 ? "No state to show" : "Nothing in it yet"} />
    </li>
  );
}

export function entryState(entry: FilePreviewEntry) {
  const shown = displayState((entry.state ?? null) as ElementState | null, { origin: entry.state ? "work" : "loaded" });
  return shown;
}

function Preview({ path, validatedOnly, onValidatedOnly, onClose }: { path: string; validatedOnly: boolean; onValidatedOnly: (on: boolean) => void; onClose: () => void }) {
  const preview = useApiQuery("file_preview", { path, validated_only: validatedOnly });
  const chapter = chapterKeyOfFile(path);
  // A file that exists, with nothing validated in it yet, is an empty view, not a missing file (UI-09). The server answers
  // with an empty preview; a refusal "not found" with Validated only on (an older server) is shown as the empty state too.
  const nothingValidated = preview.isError && validatedOnly && isRefusal(preview.error, "not_found");
  return (
    <Card aria-labelledby="preview-title" className="preview">
      <div className="preview__head row">
        <h2 id="preview-title">
          <code>{path}</code>
        </h2>
        <Button variant="quiet" onClick={onClose}>
          Close the preview
        </Button>
      </div>
      <Check
        label="Validated only"
        hint="Show only what the expert has validated."
        checked={validatedOnly}
        onChange={onValidatedOnly}
      />
      {chapter && (
        <p>
          <Link to={chapterPath(chapter)}>Open this chapter in the editor</Link>
        </p>
      )}
      {preview.isPending ? (
        <Loading>Reading the file</Loading>
      ) : nothingValidated ? (
        <PreviewBody preview={{ path, validated_only: true, entries: [], text: "" }} />
      ) : preview.isError ? (
        <FormError message={refusalText(preview.error)} />
      ) : (
        <PreviewBody preview={preview.data} />
      )}
    </Card>
  );
}

export function PreviewBody({ preview }: { preview: FilePreview }) {
  const problems = preview.messages ?? [];
  return (
    <>
      {preview.validated_only && preview.checked && (
        // the file check of the official tools ran on this view (architecture 6.2); nothing is said when it did not run
        <p className="files-check" data-passed={problems.length === 0 || undefined}>
          {problems.length === 0 ? (
            <Pill tone="note">The file check ran on this view and found nothing to fix.</Pill>
          ) : (
            <Pill tone="problem">{problems.length === 1 ? "The file check ran on this view and found 1 thing to look at." : `The file check ran on this view and found ${problems.length} things to look at.`}</Pill>
          )}
        </p>
      )}
      <CheckMessages messages={problems} summary="What the file check said" />
      <h3>What is in it</h3>
      {preview.entries.length === 0 ? (
        <EmptyState title={preview.validated_only ? "Nothing validated in this file yet" : "Nothing in this file yet"}>
          {preview.validated_only ? "Untick Validated only to see everything that is waiting." : "No element is written to it."}
        </EmptyState>
      ) : (
        <ul className="preview-entries">
          {preview.entries.map((entry, index) => {
            const shown = entryState(entry);
            return (
              <li key={`${entry.code}:${index}`} className="preview-entry">
                {entry.element_id ? <ElementLink id={entry.element_id} /> : <code>{entry.code}</code>} <code className="muted">{entry.code}</code>{" "}
                <span className="muted">{kindWord(entry.kind)}</span> {shown ? <StateBadge state={shown} /> : null}
                {entry.discipline && <span className="muted"> {entry.discipline}</span>}
                {entry.needed_by && <span className="muted"> Needed by {entry.needed_by}</span>}
              </li>
            );
          })}
        </ul>
      )}
      <h3>The file</h3>
      {/* The text of the file is shown as plain text in a box that scrolls; it is never put into the page as markup. */}
      <pre className="file-text" tabIndex={0} aria-label="The text of the file">
        {preview.text}
      </pre>
    </>
  );
}
